import { styled } from '@mui/material';

import { useMemo } from 'react';
import { Trans } from 'react-i18next';

import { Typography, Box } from '@app-velocity/ui';

import { I18nResources, useI18n } from '@app-velocity/i18n';

import { TPrototypeReportMetricConfigDto } from '@app-velocity/av-api-v2';

import { useReportConfigSettings } from '../../../../../../hooks/useReportConfigSettings';

const StyledConfig = styled('div')(({ theme }) => ({
  background: 'var(--button-button-secondary-color)',
  padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
  borderRadius: theme.spacing(1),
  display: 'inline-block',
  fontWeight: 600,
}));

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  textAlign: 'left',
}));

export type ConfigurationDescriptionProps = {
  descriptionKey?: string;
  config?: TPrototypeReportMetricConfigDto;
  scope: 'WEBSITE' | 'FIGMA_PROTOTYPE';
  mode: 'admin' | 'report';
};

const StyledTypography = styled(Typography)({
  whiteSpace: 'pre-line',
});

export const ConfigurationDescription = ({
  descriptionKey = 'prototypes:report.settings.config.editing_description',
  config,
  scope,
  mode,
}: ConfigurationDescriptionProps) => {
  const { getUsedConfigLabel, hasOwnScopedReportConfig } =
    useReportConfigSettings();
  const { t } = useI18n();

  const description = useMemo(() => {
    if (hasOwnScopedReportConfig(config, mode)) {
      return t(descriptionKey as I18nResources);
    }

    const label = getUsedConfigLabel(config ?? null, scope, mode);

    return (
      <Trans
        i18nKey="prototypes:report.settings.config.inheriting_from"
        components={{
          config: <StyledConfig>{t(label)}</StyledConfig>,
        }}
      />
    );
  }, [
    descriptionKey,
    config,
    scope,
    mode,
    getUsedConfigLabel,
    hasOwnScopedReportConfig,
    t,
  ]);

  return (
    <StyledBox>
      <StyledTypography>{description}</StyledTypography>
    </StyledBox>
  );
};
